// Reception export — RON-5469 R5. Prototype region: `#bd-export` / `buildCSV` / `downloadBlob`.
//
// ⚠️ THIS IS A CLIENT-SIDE EXPORT, AND THE SCREEN MUST NOT CLAIM OTHERWISE.
//
// The prototype's export dialog carries a GDPR notice saying "the download is recorded in the
// audit trail". A file built in the browser is recorded nowhere. §9 lists the audited,
// server-side export as **not built** — "End-of-day reconciliation, audited export ... still
// parked pending the design pass" — so the dialog here says what actually happens instead of
// repeating a promise nothing keeps.
//
// The rows come from walking `GET /board` (`fetchAllBoardRows`), so an export covers everything
// matching the current date, filter and search — not just the page on screen.

import { ROW_STATUS } from "./receptionConstants.js";

// Columns, in the order a receptionist reads them. Kept as data rather than inline so the header
// row and the value row cannot drift apart — the classic CSV bug is a column added to one and not
// the other.
export const EXPORT_COLUMNS = [
  { key: "name", header: "Name" },
  { key: "company", header: "Company" },
  { key: "email", header: "Email" },
  { key: "kind", header: "Type" },
  { key: "host", header: "Host" },
  { key: "status", header: "Status" },
  { key: "startDate", header: "Date" },
  { key: "arrivalTime", header: "Expected arrival" },
  { key: "checkedInAt", header: "Checked in" },
  { key: "checkedOutAt", header: "Signed out" },
  { key: "expectedOut", header: "Expected out" },
  { key: "isVip", header: "VIP" },
  { key: "isWalkIn", header: "Walk-in" },
  { key: "isOverstay", header: "Overstay" },
  { key: "overstayBy", header: "Overstay (mins)" },
  { key: "personnel", header: "People" },
  { key: "bookingCode", header: "Booking code" },
  { key: "purpose", header: "Purpose" },
];

const STATUS_TEXT = {
  [ROW_STATUS.EXPECTED]: "Expected",
  [ROW_STATUS.CHECKED_IN]: "Checked in",
  [ROW_STATUS.CHECKED_OUT]: "Signed out",
};

const cellValue = (row, key) => {
  const raw = row[key];

  if (raw === null || raw === undefined) return "";
  // "Yes"/"" rather than "true"/"false": a spreadsheet column of TRUE/FALSE reads as a formula
  // result, and an empty cell filters more naturally than the word "false".
  if (typeof raw === "boolean") return raw ? "Yes" : "";
  if (key === "status") return STATUS_TEXT[raw] || raw;

  return String(raw);
};

// RFC 4180. A field containing a comma, a quote or a newline is quoted, and inner quotes are
// doubled — a visitor called O'Brien is fine, one from "Smith, Jones & Co" is not without this.
export const csvEscape = (value) => {
  const text = value === null || value === undefined ? "" : String(value);

  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildCsv = (rows) => {
  const header = EXPORT_COLUMNS.map((column) => csvEscape(column.header)).join(",");
  const body = rows.map((row) =>
    EXPORT_COLUMNS.map((column) => csvEscape(cellValue(row, column.key))).join(",")
  );

  // A BOM, and CRLF line endings. Without the BOM, Excel on Windows opens a UTF-8 CSV as
  // Latin-1 and "Áine Howard" arrives as "Ãine Howard" — which is exactly the sort of thing
  // nobody notices until a name is wrong on a compliance report.
  return `﻿${[header, ...body].join("\r\n")}`;
};

export const exportFileName = ({ siteName, date, filter }) => {
  const safeSite = String(siteName || "reception").replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  return `visitors-${safeSite}-${date || "board"}${filter && filter !== "all" ? `-${filter}` : ""}.csv`;
};

// Kept separate from `buildCsv` so the CSV itself is testable without touching the DOM.
export const downloadCsv = (fileName, contents) => {
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Released on the next tick rather than immediately: revoking synchronously can cancel the
  // download in some browsers before it has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};
